import { message } from 'antd';
import { fromError } from 'zod-validation-error';
import type { ZodSchema } from 'zod';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { APIResponseType } from '@admin-panel/types';
// import { identityApi } from '../identity/identityApi';

export interface ErrorReporter {
  catchError(error: Error): void;
}

type TBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {
    zodSchema?: ZodSchema;
    mock?: boolean;
    defaultValue?: unknown;
    rawResponse?: boolean;
  },
  FetchBaseQueryMeta
>;

interface IConfig {
  passThroughChecks?: boolean;
  errorReporter?: ErrorReporter;
}

export const baseQueryWithZodValidation: (baseQuery: TBaseQuery, config: IConfig) => TBaseQuery =
  (baseQuery: TBaseQuery, { passThroughChecks, errorReporter }) =>
  async (args, api, extraOptions) => {
    /**
     * `queryResult` contains `data` and `meta` properties
     * `meta` is simply request and response objects
     * `data` is the backend response
     */
    const queryResult = await baseQuery(args, api, extraOptions);

    if (passThroughChecks) {
      return queryResult;
    }

    if (queryResult.error) {
      console.log('Error', queryResult.error);

      if (queryResult.error.status === 401) {
        // api.dispatch(identityApi.endpoints.logout.initiate());
      }

      const error = { ...queryResult.error };

      if (queryResult.error.status === 403) {
        error.data = { message: 'You are not allowed to access this resource' };
      }

      if (queryResult.error.status === 404) {
        error.data = {
          message: 'Wrong url configured. Please contact your tech team',
        };
      }
      if (queryResult.error.status === 500) {
        error.data = {
          message: 'Something went wrong. Please try again later',
        };
      }

      return { error };
    }

    const backendResponse = queryResult.data as APIResponseType<unknown>;

    if (
      Object.prototype.hasOwnProperty.call(backendResponse, 'success') &&
      !backendResponse.success
    ) {
      message.error(backendResponse.status.message);

      errorReporter?.catchError(new Error("[RTK] API DIDN'T RETURN SUCCESS"));

      return {
        error: {
          data: null,
          error: backendResponse.status?.message,
          status: backendResponse.status.code,
        },
      };
    }

    // * Check if both 'data' and 'zodSchema' are defined
    // @ts-expect-error ts does not know
    if (extraOptions?.zodSchema && backendResponse.data) {
      const parseResult = extraOptions.zodSchema.safeParse(
        // @ts-expect-error ts does not know
        backendResponse.data,
      );

      if (!parseResult.success) {
        message.error('[RTK] API CONTRACT BREACH');
        console.group(api.endpoint);
        console.log(fromError(parseResult.error));
        console.groupEnd();
        errorReporter?.catchError(
          new Error(`[RTK] API CONTRACT BREACH ${fromError(parseResult.error).message}`),
        );
        // Toast.ERROR(parseResult.error.message);
      }
    }

    /**
     * return with `status`, `serverTime`, `success` and `data` properties
     * exactly as they are in the backend response
     */
    if (extraOptions?.rawResponse) {
      return queryResult;
    }

    /**
     *  return without `status`, `serverTime`, `success` and `data` properties
     *  only `data` property is returned
     */
    // @ts-expect-error ts does not know
    queryResult.data = backendResponse.data;

    return queryResult;
  };
